import { isValidCheckNumber, seededCheckStart, shiftCheckNumber, checkGap } from './checkNumbers';
// Pure date + amount helpers for "Update Existing Paystub" (re-date flow).
export const PERIODS = { weekly: 52, 'bi-weekly': 26, 'semi-monthly': 24, monthly: 12 };

export const normFreq = (f) => {
  const s = String(f || '').toLowerCase();
  if (s.includes('semi')) return 'semi-monthly';
  if (s.includes('bi')) return 'bi-weekly';
  if (s.includes('week')) return 'weekly';
  if (s.includes('month')) return 'monthly';
  return 'bi-weekly';
};

export const parseISO = (s) => {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12);
};

export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const lastDay = (y, m) => new Date(y, m + 1, 0).getDate();
const isEom = (d) => d.getDate() === lastDay(d.getFullYear(), d.getMonth());

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12);

const addMonths = (d, n) => {
  const t = new Date(d.getFullYear(), d.getMonth() + n, 1, 12);
  const max = lastDay(t.getFullYear(), t.getMonth());
  t.setDate(isEom(d) ? max : Math.min(d.getDate(), max));
  return t;
};

// Semi-monthly: move by k half-months. 15th / end-of-month act as boundary markers.
const addHalfMonths = (d, k) => {
  const half = d.getDate() <= 15 ? 0 : 1;
  const boundary = (half === 0 && d.getDate() === 15) || (half === 1 && isEom(d));
  const off = half === 0 ? d.getDate() : d.getDate() - 15;
  const total = half + k;
  const t = new Date(d.getFullYear(), d.getMonth() + Math.floor(total / 2), 1, 12);
  const newHalf = ((total % 2) + 2) % 2;
  const max = lastDay(t.getFullYear(), t.getMonth());
  if (newHalf === 0) t.setDate(boundary ? 15 : Math.min(off, 15));
  else t.setDate(boundary ? max : Math.min(off + 15, max));
  return t;
};

export const stepDate = (d, k, freq) => {
  if (freq === 'monthly') return addMonths(d, k);
  if (freq === 'semi-monthly') return addHalfMonths(d, k);
  return addDays(d, k * (freq === 'weekly' ? 7 : 14));
};

const stepPeriod = (anchor, k, freq) => ({
  payDate: stepDate(anchor.payDate, k, freq),
  periodStart: stepDate(anchor.periodStart, k, freq),
  periodEnd: stepDate(anchor.periodEnd, k, freq),
});

// Stub # in the year = 1 + how many earlier pay dates on this schedule fall in the same year.
export const stubNumberInYear = (payDate, freq) => {
  let n = 1;
  while (n < 60 && stepDate(payDate, -n, freq).getFullYear() === payDate.getFullYear()) n += 1;
  return n;
};

export const buildAnchor = ({ payDate, periodStart, periodEnd }, freq) => {
  const pd = parseISO(payDate);
  if (!pd) return null;
  const days = freq === 'weekly' ? 7 : freq === 'bi-weekly' ? 14 : freq === 'monthly' ? 30 : 15;
  const pe = parseISO(periodEnd) || addDays(pd, -5);
  const ps = parseISO(periodStart) || addDays(pe, -(days - 1));
  return { payDate: pd, periodStart: ps, periodEnd: pe };
};

// Latest scheduled pay date on/before asOf → returns k offset from anchor.
export const latestOffset = (anchor, asOf, freq) => {
  let k = 0;
  if (anchor.payDate <= asOf) {
    while (k < 3000 && stepDate(anchor.payDate, k + 1, freq) <= asOf) k += 1;
  } else {
    while (k > -3000 && stepDate(anchor.payDate, k, freq) > asOf) k -= 1;
  }
  return k;
};

// Is `date` exactly on the uploaded schedule? Returns nearest scheduled date too.
export const scheduleCheck = (anchor, date, freq) => {
  const k = latestOffset(anchor, date, freq);
  const before = stepDate(anchor.payDate, k, freq);
  const after = stepDate(anchor.payDate, k + 1, freq);
  const nearest = (date - before) <= (after - date) ? before : after;
  return { onSchedule: toISO(before) === toISO(date), nearest: toISO(nearest) };
};

// Produce N periods (index 0 = most recent) for either mode.
export const projectPeriods = ({ anchor, freq, mode, asOf, targetDate, count }) => {
  let base;
  let kBase = 0;
  if (mode === 'next') {
    base = anchor;
    kBase = count;
  } else if (mode === 'specific') {
    const t = parseISO(targetDate);
    if (!t) return [];
    const endLag = Math.round((anchor.payDate - anchor.periodEnd) / 86400000);
    const len = Math.round((anchor.periodEnd - anchor.periodStart) / 86400000);
    const pe = addDays(t, -endLag);
    base = { payDate: t, periodEnd: pe, periodStart: addDays(pe, -len) };
  } else {
    const a = parseISO(asOf);
    if (!a) return [];
    kBase = latestOffset(anchor, a, freq);
    base = anchor;
  }
  return Array.from({ length: count }, (_, i) => {
    const k = kBase - i;
    const p = mode === 'specific' ? stepPeriod(base, -i, freq) : stepPeriod(base, k, freq);
    return {
      offset: mode === 'specific' ? null : k,
      payDate: toISO(p.payDate),
      periodStart: toISO(p.periodStart),
      periodEnd: toISO(p.periodEnd),
      stubNumber: stubNumberInYear(p.payDate, freq),
    };
  });
};

const r2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
const YTD_KEYS = ['grossPay', 'federalTax', 'stateTax', 'socialSecurity', 'medicare', 'sdi', 'otherDeductions', 'netPay'];

export const buildRedatedStubs = (sourceStub, periods, floor = 0) => {
  const cur = { ...(sourceStub.current || {}) };
  const extras = ['overtime', 'bonus', 'commission', 'tips', 'holidayPay', 'sickPay', 'vacationPay']
    .reduce((t, k) => t + (Number(cur[k]) || 0), 0);
  if (!(Number(cur.regular) > 0) && Number(cur.grossPay) > 0) cur.regular = r2(cur.grossPay - extras);
  if (!(Number(cur.grossPay) > 0) && Number(cur.regular) > 0) cur.grossPay = r2(Number(cur.regular) + extras);
  cur.totalTaxes = r2((cur.federalTax || 0) + (cur.stateTax || 0) + (cur.socialSecurity || 0) + (cur.medicare || 0) + (cur.sdi || 0));
  cur.totalDeductions = r2(cur.totalTaxes + (cur.otherDeductions || 0));
  if (!cur.netPay) cur.netPay = r2((cur.grossPay || 0) - cur.totalDeductions);
  const rawCheck = String(sourceStub.checkNumber || '').replace(/\D/g, '');
  const baseCheck = isValidCheckNumber(rawCheck) ? Number(rawCheck) : seededCheckStart(sourceStub.payDate);
  let nums = periods.map((p, i) => shiftCheckNumber(baseCheck, p.offset ?? (periods.length - i)));
  // Every new number must sit above the company counter (numbers already used by any employee).
  if (floor && Math.min(...nums) <= floor) {
    let n = floor;
    const asc = [];
    for (let j = periods.length - 1; j >= 0; j--) { n += checkGap(floor, j); asc.push(n); }
    nums = asc.reverse();
  }
  return periods.map((p, i) => {
    const ytd = {};
    YTD_KEYS.forEach((k) => { ytd[k] = r2((cur[k] || 0) * p.stubNumber); });
    const checkNumber = String(nums[i]);
    return {
      stubNumber: p.stubNumber,
      stubNumberInYear: p.stubNumber,
      checkNumber,
      payDate: p.payDate,
      periodStart: p.periodStart,
      periodEnd: p.periodEnd,
      current: { ...cur },
      ytd,
    };
  });
};

export const suggestTemplateId = (layout = {}) => {
  const style = String(layout.template_style || '').toLowerCase();
  if (style === 'voucher' || layout.has_check_stub) return 3;
  if (style === 'modern') return 2;
  if (style === 'corporate' || style === 'statement') return 6;
  return 1;
};
